import fetch from 'cross-fetch'
import ApolloClient from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { setContext } from 'apollo-link-context'
import { fromPromise, split } from 'apollo-link'
import { onError } from "apollo-link-error"
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { getMainDefinition } from 'apollo-utilities'
import introspectionQueryResultData from './fragmentTypes.json'
import cfg from '@/cfg'
import Vue from 'vue'
import { createClient } from 'graphql-ws'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import ws from 'ws'
import * as Sentry from "@sentry/vue"

let refreshTokenRequest = null

const refreshAccessToken = async () => {
  const refreshToken = window.localStorage.getItem("refreshSession")
  if (!refreshToken) {
    console.warn("[AuthDebug][Apollo] refresh skipped: missing refresh token")
    throw new Error("Missing refresh token")
  }
  console.warn("[AuthDebug][Apollo] attempting refreshSession mutation")
  const response = await fetch(cfg.graphqlEndPoint(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      query: `
        mutation RefreshSession($refreshToken: String!) {
          refreshSession(refreshToken: $refreshToken) {
            jwToken
            accessToken
            refreshToken
            error
            message
          }
        }
      `,
      variables: { refreshToken },
    }),
  })
  if (!response.ok) {
    console.warn("[AuthDebug][Apollo] refresh request failed", { status: response.status })
    throw new Error(`Refresh failed with ${response.status}`)
  }
  const parsed = await response.json()
  const payload = parsed && parsed.data && parsed.data.refreshSession
  if (!payload || payload.error || !(payload.accessToken || payload.jwToken)) {
    console.warn("[AuthDebug][Apollo] refresh payload invalid", {
      hasPayload: !!payload,
      error: payload && payload.error,
      message: payload && payload.message,
    })
    throw new Error((payload && payload.message) || "Refresh failed")
  }
  const nextAccessToken = payload.accessToken || payload.jwToken
  window.localStorage.setItem("session", nextAccessToken)
  if (payload.refreshToken) {
    window.localStorage.setItem("refreshSession", payload.refreshToken)
  }
  console.info("[AuthDebug][Apollo] refresh succeeded; retrying original operation")
  return nextAccessToken
}

const graphqlCallLabel = (uri, options) => {
  let kind = ""
  let operation = ""
  try {
    const raw = options && options.body
    const parsed = typeof raw === "string" ? JSON.parse(raw) : null
    if (parsed && parsed.operationName) operation = String(parsed.operationName)
    const query = parsed && typeof parsed.query === "string" ? parsed.query : ""
    const match = query.match(/\b(query|mutation|subscription)\s+([A-Za-z0-9_]+)/)
    if (match) {
      kind = match[1]
      if (!operation) operation = match[2]
    }
  } catch (e) {
    kind = ""
  }
  const name = [kind, operation].filter(function (part) { return part }).join(" ")
  if (name) return name + " " + uri
  return String(uri || "")
}

// Custom fetch wrapper to intercept responses and capture HTML errors
const fetchWithErrorCapture = async (uri, options) => {
  const requestUrl = uri
  const requestStartTime = Date.now()
  
  // Add breadcrumb for the request
  Sentry.addBreadcrumb({
    category: 'http',
    message: `GraphQL request to ${requestUrl}`,
    level: 'info',
    data: {
      url: requestUrl,
      method: (options && options.method) || 'POST'
    }
  })
  
  // Extract infrastructure headers for debugging
  const extractInfrastructureHeaders = (headers) => {
    const infraHeaders = {}
    const headerMap = Object.fromEntries(headers.entries())
    
    // Cloudflare headers
    if (headerMap['cf-ray']) infraHeaders.cfRay = headerMap['cf-ray']
    if (headerMap['cf-request-id']) infraHeaders.cfRequestId = headerMap['cf-request-id']
    if (headerMap['cf-visitor']) infraHeaders.cfVisitor = headerMap['cf-visitor']
    if (headerMap['cf-connecting-ip']) infraHeaders.cfConnectingIp = headerMap['cf-connecting-ip']
    if (headerMap['cf-ipcountry']) infraHeaders.cfIpCountry = headerMap['cf-ipcountry']
    
    // Proxy/load balancer headers
    if (headerMap['x-forwarded-for']) infraHeaders.xForwardedFor = headerMap['x-forwarded-for']
    if (headerMap['x-forwarded-proto']) infraHeaders.xForwardedProto = headerMap['x-forwarded-proto']
    if (headerMap['x-forwarded-host']) infraHeaders.xForwardedHost = headerMap['x-forwarded-host']
    if (headerMap['x-real-ip']) infraHeaders.xRealIp = headerMap['x-real-ip']
    if (headerMap['via']) infraHeaders.via = headerMap['via']
    
    // Server identification
    if (headerMap['server']) infraHeaders.server = headerMap['server']
    if (headerMap['x-powered-by']) infraHeaders.xPoweredBy = headerMap['x-powered-by']
    
    // Kubernetes/container headers
    if (headerMap['x-request-id']) infraHeaders.xRequestId = headerMap['x-request-id']
    if (headerMap['x-trace-id']) infraHeaders.xTraceId = headerMap['x-trace-id']
    
    return infraHeaders
  }
  
  try {
    const response = await fetch(uri, options)
    const requestDuration = Date.now() - requestStartTime
    
    // Clone the response so we can read it without consuming the original
    const clonedResponse = response.clone()
    
    // Check if response is HTML instead of JSON
    const contentType = response.headers.get('content-type') || ''
    const isHtml = contentType.includes('text/html') || contentType.includes('text/plain')
    
    // If it's HTML or we get an error status, capture the response body
    if (isHtml || !response.ok) {
      try {
        const text = await clonedResponse.text()
        // Capture full response body for HTML errors (limit to 50KB to avoid huge payloads)
        const maxBodySize = 50000
        const fullResponseBody = text.length > maxBodySize 
          ? text.substring(0, maxBodySize) + `\n\n[Response truncated - total size: ${text.length} bytes]`
          : text
        
        // Extract key information from HTML response
        let htmlSource = 'unknown'
        if (fullResponseBody.includes('Cloudflare')) {
          htmlSource = 'cloudflare'
        } else if (fullResponseBody.includes('nginx') || fullResponseBody.match(/nginx\/[\d.]+/i)) {
          htmlSource = 'nginx'
        } else if (fullResponseBody.includes('Apache') || fullResponseBody.match(/Apache\/[\d.]+/i)) {
          htmlSource = 'apache'
        } else if (fullResponseBody.includes('Kubernetes') || fullResponseBody.includes('ingress')) {
          htmlSource = 'kubernetes_ingress'
        } else if (fullResponseBody.includes('502 Bad Gateway') || fullResponseBody.includes('503 Service Unavailable')) {
          htmlSource = 'proxy_error'
        } else if (fullResponseBody.includes('login') || fullResponseBody.includes('authentication')) {
          htmlSource = 'auth_redirect'
        }
        
        // If we got HTML instead of JSON, this is a problem
        if (isHtml || text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          const error = new Error(`Network error: Server returned HTML instead of JSON. Status: ${response.status} ${response.statusText}. Call: ${graphqlCallLabel(uri, options)}`)
          error.name = 'HTMLResponseError'
          error.requestUrl = requestUrl
          error.statusCode = response.status
          error.statusText = response.statusText
          error.responseBody = fullResponseBody
          error.htmlSource = htmlSource
          
          // Extract all response headers
          const allResponseHeaders = Object.fromEntries(response.headers.entries())
          const infrastructureHeaders = extractInfrastructureHeaders(response.headers)
          
          // Add breadcrumb for the error response
          Sentry.addBreadcrumb({
            category: 'http',
            message: `HTML response received instead of JSON`,
            level: 'error',
            data: {
              url: requestUrl,
              status: response.status,
              statusText: response.statusText,
              htmlSource: htmlSource,
              contentType: contentType
            }
          })
          
          // Report to Sentry immediately with full context
          Sentry.captureException(error, {
            tags: {
              errorType: "html_response_error",
              requestUrl: requestUrl,
              statusCode: String(response.status),
              contentType: contentType,
              htmlSource: htmlSource,
              ...(infrastructureHeaders.cfRay && { cfRay: infrastructureHeaders.cfRay }),
              ...(infrastructureHeaders.server && { server: infrastructureHeaders.server })
            },
            extra: {
              requestUrl: requestUrl,
              statusCode: response.status,
              statusText: response.statusText,
              contentType: contentType,
              requestDuration: `${requestDuration}ms`,
              htmlSource: htmlSource,
              
              // Full response body (up to 50KB)
              fullResponseBody: fullResponseBody,
              responseBodyLength: text.length,
              
              // All response headers
              responseHeaders: allResponseHeaders,
              
              // Infrastructure-specific headers
              infrastructure: infrastructureHeaders,
              
              // Request details
              requestMethod: (options && options.method) || 'POST',
              requestHeaders: (options && options.headers) || {},
              
              // Additional context
              responseUrl: response.url || requestUrl, // May differ if redirected
              redirected: response.url !== requestUrl,
              ok: response.ok,
              type: response.type
            }
          })
          
          // Throw the error so Apollo can handle it
          throw error
        }
      } catch (textError) {
        // If we can't read the response, still report the error
        if (textError.name !== 'HTMLResponseError') {
          const error = new Error(`Network error: Failed to parse response. Status: ${response.status} ${response.statusText}`)
          error.name = 'ResponseParseError'
          error.requestUrl = requestUrl
          error.statusCode = response.status
          error.statusText = response.statusText
          
          const infrastructureHeaders = extractInfrastructureHeaders(response.headers)
          
          Sentry.captureException(error, {
            tags: {
              errorType: "response_parse_error",
              requestUrl: requestUrl,
              statusCode: String(response.status)
            },
            extra: {
              requestUrl: requestUrl,
              statusCode: response.status,
              statusText: response.statusText,
              parseError: textError.message,
              responseHeaders: Object.fromEntries(response.headers.entries()),
              infrastructure: infrastructureHeaders
            }
          })
        }
        throw textError
      }
    }
    
    return response
  } catch (error) {
    // If it's already our custom error, re-throw it
    if (error.name === 'HTMLResponseError' || error.name === 'ResponseParseError') {
      throw error
    }
    
    // For other network errors, enhance with URL context
    if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network error'))) {
      error.requestUrl = requestUrl
      const requestDuration = Date.now() - requestStartTime
      
      Sentry.captureException(error, {
        tags: {
          errorType: "network_fetch_error",
          requestUrl: requestUrl
        },
        extra: {
          requestUrl: requestUrl,
          requestMethod: (options && options.method) || 'POST',
          requestHeaders: (options && options.headers) || {},
          requestDuration: `${requestDuration}ms`,
          errorMessage: error.message
        }
      })
    }
    
    throw error
  }
}

const httpLink = createHttpLink({
  uri: cfg.graphqlEndPoint(),
  fetch: fetchWithErrorCapture
})

const websocket = process.env.NODE_ENV === "test" ? ws : null // Websocket implementation for tests

console.log({endpoint: cfg.subscriptionEndPoint()})
const wsLink = new GraphQLWsLink(
  createClient({
    retryAttempts: Infinity,
    shouldRetry: () => true,
    keepAlive: 10000,
    url: cfg.subscriptionEndPoint(),
    connectionParams: () => {
      const session = window.localStorage.getItem("session")
      return session ? { Authorization: `Bearer ${session}` } : {}
    },
    webSocketImpl: websocket,
    on: {
      connected: (socket, payload, wasRetry) => {
        console.log("[WS] CONNECTED", { payload, wasRetry, url: cfg.subscriptionEndPoint() })
        try {
          if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(
              new CustomEvent("cb-graphql-ws-connected", { detail: { wasRetry: !!wasRetry } })
            )
          }
        } catch (e) {
          console.warn("[WS] cb-graphql-ws-connected dispatch failed", e)
        }
      },
      closed: (socket, evt) => {
        console.log("[WS] CLOSED/DISCONNECTED", { url: cfg.subscriptionEndPoint() })
        try {
          if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(
              new CustomEvent("cb-graphql-ws-closed", {
                detail: { code: evt && evt.code, reason: evt && evt.reason },
              })
            )
          }
        } catch (e) {
          console.warn("[WS] cb-graphql-ws-closed dispatch failed", e)
        }
      },
      connecting: (socket, isRetry) => {
        console.log("[WS] CONNECTING", { isRetry, url: cfg.subscriptionEndPoint() })
        try {
          if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(
              new CustomEvent("cb-graphql-ws-connecting", { detail: { isRetry: !!isRetry } })
            )
          }
        } catch (e) {
          console.warn("[WS] cb-graphql-ws-connecting dispatch failed", e)
        }
      },
      ping: (received, payload) => {
        console.log("[WS] PING", { received, payload })
      },
      pong: (received, payload) => {
        console.log("[WS] PONG", { received, payload })
      },
      error: (error) => {
        console.log("[WS] ERROR", { error, message: error && error.message, url: cfg.subscriptionEndPoint() })
      },
      message: (message) => {
        const type = message && message.type
        const id = message && message.id
        const payload = message && message.payload
        if (type === "next" && payload && payload.data) {
          const keys = payload.data && typeof payload.data === "object" ? Object.keys(payload.data) : []
          console.log("[WS] RECEIVE", { type, id, subscriptionKeys: keys, hasData: !!payload.data })
        } else {
          console.log("[WS] MESSAGE", { type, id, url: cfg.subscriptionEndPoint() })
        }
      },
      opened: (socket) => {
        console.log("[WS] OPENED", { url: cfg.subscriptionEndPoint() })
      },
    }
  })
)
// const wsLink = undefined

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = window.localStorage.getItem('session')
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ""
    }
  }
})

const isAuthErrorMessage = (message) => {
  return message &&
    (message.includes("You must login to do that") || message.includes("Unauthorized") || message.includes("UNAUTHENTICATED"))
}

const fingerprintGraphQLMessage = (message) => {
  if (!message) return "unknown"
  const variableMatch = message.match(/Variable "(\$[^"]+)"/)
  if (variableMatch) {
    if (message.indexOf("got invalid value") !== -1) {
      return variableMatch[1] + " invalid value"
    }
    if (message.indexOf("non-null") !== -1 || message.indexOf("not provided") !== -1) {
      return variableMatch[1] + " missing"
    }
    return variableMatch[1]
  }
  const fieldMatch = message.match(/field ([A-Za-z0-9_.]+)/i)
  if (fieldMatch) {
    return fieldMatch[1]
  }
  return message
}

const errorLink = onError(({ graphQLErrors, networkError, response, operation, forward }) => {
  const graphqlEndpoint = cfg.graphqlEndPoint()
  const operationName = (operation && operation.operationName) || "unknown"
  const isRefreshOperation = operationName === "RefreshSession"
  const payloadErrors =
    (response && response.errors) ||
    (networkError && networkError.result && networkError.result.errors) ||
    []
  const allGraphQLErrors = (graphQLErrors || []).concat(payloadErrors)
  const graphQLErrorMessages = allGraphQLErrors.map((e) => (e && e.message) || "")
  const isAuthGraphQLError = graphQLErrorMessages.some((message) => isAuthErrorMessage(message))
  const networkStatusCode = networkError && (networkError.statusCode || (networkError.response && networkError.response.status))
  const isAuthNetworkError = networkStatusCode === 401

  if (!isRefreshOperation && (isAuthGraphQLError || isAuthNetworkError)) {
    console.warn("[AuthDebug][Apollo] auth error detected, preparing refresh", {
      operationName,
      isAuthGraphQLError,
      isAuthNetworkError,
      networkStatusCode,
      graphQLErrorMessages,
    })
    if (!refreshTokenRequest) {
      refreshTokenRequest = refreshAccessToken().finally(() => {
        refreshTokenRequest = null
      })
    }
    return fromPromise(
      refreshTokenRequest.catch(() => {
        console.warn("[AuthDebug][Apollo] refresh failed; redirecting to /login")
        window.localStorage.removeItem("session")
        window.localStorage.removeItem("refreshSession")
        Vue.router.push("/login")
        return null
      })
    )
      .filter(Boolean)
      .flatMap((newToken) => {
        console.info("[AuthDebug][Apollo] refresh resolved; forwarding operation with new token", { operationName })
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${newToken}`,
          },
        }))
        return forward(operation)
      })
  }
  
  // Handle GraphQL errors
  if (graphQLErrors && graphQLErrors.length) {
    // Do not redirect purely because local JWT looks expired here.
    // During refresh windows, non-auth GraphQL errors can arrive (e.g. route-leave mutations),
    // and forcing /login races with the refresh flow.
    if (!isAuthGraphQLError) {
      console.debug("[AuthDebug][Apollo] non-auth GraphQL errors observed; skipping login redirect", {
        operationName,
        graphQLErrorMessages,
      })
    }
    
    // Report GraphQL errors to Sentry with URL context.
    // Fingerprint by operation + path so variable/schema errors do not collapse into one issue.
    graphQLErrors.forEach(({ message, locations, path }) => {
      const pathKey = Array.isArray(path) ? path.join(".") : String(path || "unknown")
      const fingerprintMessage = fingerprintGraphQLMessage(message)
      Sentry.captureException(new Error(`GraphQL Error: ${message}`), {
        fingerprint: ["graphql", operationName, pathKey, fingerprintMessage],
        tags: {
          errorType: "graphql_error",
          url: graphqlEndpoint,
          operationName,
          graphQLPath: pathKey
        },
        extra: {
          graphQLError: {
            message,
            locations,
            path,
            operationName: (operation && operation.operationName),
            query: (operation && operation.query && operation.query.loc && operation.query.loc.source && operation.query.loc.source.body)
          },
          requestUrl: graphqlEndpoint
        }
      })
    })
  }
  
  // Handle network errors (failed HTTP requests)
  if (networkError) {
    const errorMessage = networkError.message || String(networkError)
    // Use the requestUrl from the error if available (set by our custom fetch), otherwise fall back to endpoint
    const requestUrl = networkError.requestUrl || graphqlEndpoint
    const operationName = (operation && operation.operationName) || "unknown"
    
    // Extract additional error details
    let statusCode = null
    let statusText = null
    let responseBody = null
    let fullResponseBody = null
    let htmlSource = null
    
    if (networkError.statusCode) {
      statusCode = networkError.statusCode
    } else if (networkError.response && networkError.response.status) {
      statusCode = networkError.response.status
    }
    
    if (networkError.statusText) {
      statusText = networkError.statusText
    } else if (networkError.response && networkError.response.statusText) {
      statusText = networkError.response.statusText
    }
    
    if (networkError.result) {
      responseBody = networkError.result
    }
    
    // Check if this is an HTML response error (from our custom fetch wrapper)
    if (networkError.name === 'HTMLResponseError' || networkError.name === 'ResponseParseError') {
      fullResponseBody = networkError.responseBody || networkError.fullResponseBody
      htmlSource = networkError.htmlSource
      statusCode = networkError.statusCode || statusCode
      statusText = networkError.statusText || statusText
    }
    
    // Check if error message indicates HTML response
    const isHtmlResponseError = errorMessage.includes('HTML instead of JSON') || 
                                errorMessage.includes("Unexpected token '<'") ||
                                errorMessage.includes('<!DOCTYPE')
    
    // Report network errors to Sentry with full URL context
    Sentry.captureException(networkError, {
      tags: {
        errorType: isHtmlResponseError ? "html_response_error" : "network_error",
        requestUrl: requestUrl,
        operationName: operationName,
        statusCode: statusCode ? String(statusCode) : "unknown",
        ...(htmlSource && { htmlSource: htmlSource })
      },
      extra: {
        networkError: {
          message: errorMessage,
          statusCode,
          statusText,
          responseBody,
          ...(fullResponseBody && { fullResponseBody: fullResponseBody }),
          ...(htmlSource && { htmlSource: htmlSource })
        },
        requestUrl: requestUrl,
        operationName: operationName,
        operationQuery: (operation && operation.query && operation.query.loc && operation.query.loc.source && operation.query.loc.source.body),
        operationVariables: (operation && operation.variables),
        ...(isHtmlResponseError && {
          htmlResponseDetected: true,
          note: "Server returned HTML instead of JSON. Check fullResponseBody in extra data to identify the source (Cloudflare, nginx, Kubernetes ingress, etc.)."
        })
      }
    })
  }
})

const link = errorLink.concat(httpLink)

const generateLink = () => {
  if (process.env.NODE_ENV === "test" || window.location.pathname.includes('/scangui/')) {
    return authLink.concat(httpLink)
  } else {
    return split(
      ({ query }) => {
        const def = getMainDefinition(query)
        return def.kind === "OperationDefinition" && def.operation === "subscription"
      },
      wsLink,
      authLink.concat(link)
    )
  }
}

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache', // SM: Todo - investigate caching of timeline documents!
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache', // SM: Todo - investigate caching of timeline documents!
    errorPolicy: 'all'
  }
}

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData
})

let apollo = null
if (!apollo) {
  apollo = new ApolloClient({
    link: generateLink(),
    cache: new InMemoryCache({ fragmentMatcher }),
    defaultOptions
  })  
}
export default apollo
